import { Company, User } from '../types/freshchat'
import { IReportingNotification, IReportingNotificationViewed } from '../util/notification/notification'

import { ActiveUser } from '../models/editor_active_viewer'
import { AuthUser } from '../models'
import { CollapsibleItemAction } from './conversationV2'
import { ConversationProvider } from '../models/api/ConversationChannel'
import { IBuilderSettings } from '../models/membership'
import Team from '../models/team'

export interface UserState {
  user?: { [key: string]: any }
}
export interface CalendarState {
  calendars: { [key: string]: any }[]
  locationId: undefined | string
  subAccountConfig: any
  services: { [key: string]: any }[]
  servicesLocationId: undefined | string
}

export interface ScoreProfileState {
  profiles: { [key: string]: any }[]
  locationId: undefined | string
}

export interface LinkedCalendarState {
  linkedCalendars: { [key: string]: any }[]
  userId: undefined | string
  locationId: undefined | string
}

export interface SMTPServiceState {
  locationSMTPServices: { [key: string]: any }[]
  companySMTPServices: { [key: string]: any }[]
  locationId: undefined | string
  companyId: undefined | string
}
export interface MailGunServiceState {
  mailgunLocationWithErrors: string
  mailgunCompanyWithErrors: string
  locationId: undefined | string
  companyId: undefined | string
  locationMailgunAccount: undefined | { [key: string]: any }
  companyMailgunAccount: undefined | { [key: string]: any }
}
export interface DefaultEmailService {
  defaultEmailService: { [key: string]: any }
  usingTheirOwnEmailService: boolean
}
export interface Step {
  funnelId: string
  url: string
  value: string
  sequence: number
  name: string
}
export interface FunnelSteps {
  [key: number]: Step
}
export interface FunnelState {
  funnelSteps: FunnelSteps
  editorActiveViewers: Array<ActiveUser>
}
export interface CompanyState {
  company?: { [key: string]: any }
  whiteLabelUrl?: string
  isWhiteLabelled?: boolean
}

export interface ConversationAiState {
  isConversationAiEnabled: Boolean | undefined
  trialConversationList: { [key: string]: any }
  aiSettings: { [key: string]: any }
  locationConversationAiEnabling: { [key: string]: Boolean }
  aiEmployeeSettings: { [key: string]: any }
}

export interface ProspectingState {
  optInEnabled: Boolean | undefined
  offeringEnabled: Boolean | undefined
  linkedSubAccountId: String | undefined
}

export interface AIAgentState {
  isAIAgentEnabled: Boolean | undefined
}

export interface AiCompanyState {
  isSeasonOfAIEnabled: Boolean | undefined
  updatedAt: number
  seasonOfAI: object
  affiliateCode: string
  featureFlags: {
    flags: {
      GlobalSOAIEnabled: boolean,
      SummerOfAIEnabled: boolean,
    }
    companyId: string
  }
}

enum PlanType {
  USAGE = 'USAGE',
  SUBSCRIPTION = 'SUBSCRIPTION'
}

export enum PurchaseType {
  AgencyDirectPurchase = 'COMPANY_DIRECT_PURCHASE',
  LocationPurchase = 'LOCATION_PURCHASE'
}

export interface AiLocationState {
  isSeasonOfAIOptIn: Boolean | undefined,
  plan: {
      type: PlanType,
      purchaseStyle?: PurchaseType,
      subscriptionId?: string,
      amount?: number,
      currency?: string,
      paymentStatus?: string,
      affiliateCode?: string,
      startedAt?: string | number
  },
  isSOADisabled: boolean,
  seasonOfAI: {
    disableSOA: boolean
  },
  showSummerOfAIMenu: boolean,
  locationPlans: { [key: string]: any },
  locationSeasonOfAI: { [key: string]: any },
  locationOptIn: { [key: string]: boolean },
  locationShowMenu: { [key: string]: boolean }
}

export interface RootState {
  user: any
  locations: any
  deviceId?: string
  initialized: boolean
  collapseSideBar: boolean
  locationLoaderActive: boolean
  agencyLoaderActive: boolean
  loaderCount: number
  manualCollapseSidebar: boolean
  isAdvancedCalendarView: boolean
  twilioClientReady: boolean
  menuTabs?: { [key: string]: any }
  currentContacts?: number
}
export interface AuthState {
  user?: AuthUser
  locationId: undefined | string
  isUserSwitching: boolean
  isTokenRefreshing: boolean
}
export interface EstimateState {
  estimates: { [key: string]: any }
}
export interface ContactsState {
  contacts: { [key: string]: any }
  deletedContacts: { [key: string]: any }
  ids: Set<string>
  locationId: undefined | string
}
export interface LocationState {
  locationsLoaded?: boolean
  locations: { [key: string]: any }[]
  activeLocations: string[]
  notificationLocations: string[]
  currentLocation: any
  phoneNumbers?: any
  whatsAppNumbers?: any
}

export interface UsersState {
  locationId: string | undefined
  users: any[]
}

export interface VuexConversation {
  done: boolean
  last_document_date: string | number
  documents: Array<{ [key: string]: any }>
  total?: number
}

export interface ConversationState {
  all: VuexConversation
  inbox: VuexConversation
  unread: VuexConversation
  search: VuexConversation
  currentTab: string
  searchTerm: string
  fetching: boolean
  searching: boolean
  triggerLinksMenuItem: any
  filters: any
  filtersV2: any
  onboarding: boolean
  popupStep: number
  currentTabV2: string
  resultsFetchedFromSearch: boolean
  outboundSentConversationIds: string[]
  newMessageData: any
  userSavedFilters: any
  gmbIntegration: any
  linkedPages: any[]
  collapseContent: boolean
  draftMessages: any
  snippets: any[]
  phoneNumbers: any[]
  whatsAppNumbers: any[]
  currentLeftMostTab?: CollapsibleItemAction
  currentConversationId?: string
  newGroupConversationData?: any
  activeLeftMostPanelTab?: string
  connectedFacebookPages?: { [key: string]: any }
}

export interface CampaignState {
  campaigns: { [key: string]: any }
  locationId: undefined | string
}

export interface WorkflowState {
  workflows: { [key: string]: any }
  locationId: undefined | string
  isWorkflowResellingEnabled: boolean
  locationWorkflowResellingEnabling: { [key: string]: boolean }
}

export interface PipelineState {
  pipelines: { [key: string]: any }
  locationId: undefined | string
  isLoading: boolean
}

export interface LocationCustomisation {
  affiliateName: string
  titleName: string
  campaignName: string
  mediaName: string
  payoutName: string
  isLoaded: boolean
}
export interface AffiliateManager {
  locationCustomisation: LocationCustomisation
}

export interface AgencyUsersState {
  users: { [key: string]: any }
  locationId: undefined | string
}

export interface ReviewAggregateData {
  totalReviews: number
  totalRating: number
  breakdown: {
    '1': number
    '2': number
    '3': number
    '4': number
    '5': number
  }
  positiveReviews: 0
  negativeReviews: 0
  sources: number[]
}
export interface ReviewComponentData {
  previous: ReviewAggregateData
  current: ReviewAggregateData
  fetched: boolean
}
export interface ReviewAggregateState {
  thisWeek: ReviewComponentData
  lastWeek: ReviewComponentData
  thisMonth: ReviewComponentData
  last6Months: ReviewComponentData
  thisYear: ReviewComponentData
}
export interface ReviewRequestAggregateData {
  totalRequests: number
  leaderboard: { [key: string]: number }
}
export interface ReviewRequestComponentData {
  current: ReviewRequestAggregateData
  fetched: boolean
}
export interface ReviewRequestAggregateState {
  thisWeek: ReviewRequestComponentData
  lastWeek: ReviewRequestComponentData
  thisMonth: ReviewRequestComponentData
  last6Months: ReviewRequestComponentData
  thisYear: ReviewRequestComponentData
}
export interface ImagePreviewState {
  src: string
}
export interface ManualCallStatusState {
  manualCallId: string
}
export interface PhoneNumbers {
  dialOutNumber: string
  numbers: string[]
}
export interface whatsAppNumbers {
  dialOutNumber: string
  numbers: string[]
}
export interface IncomingCall {
  from: string
  to: string
  contactId: string
  newCustomer: boolean
  appointmentId?: string
  locationId: string
}
export interface IncomingCallState {
  incomingCall?: IncomingCall
  available: boolean
}
export interface JobsState {
  jobs: { [key: string]: any }
}
export interface ContactState {
  estimates: Array<{ [key: string]: any }>
}

export interface ContactsState {
  contacts: { [key: string]: any }
  locationId: undefined | string
  ids: Set<string>
}
export interface TasksState {
  tasks: { [key: string]: any }
}

export interface Oauth2State {
  oauth2: any[]
  locationId: undefined | string
  isReady: boolean
}

export interface OpportunityState {
  opportunities: any[]
  locationId: undefined | string
  filters: any
  isReady: boolean
  opportunitiesStageData: any
  loadingAggregates: boolean
}

export interface FiltersState {
  filters: any
  locationId: undefined | string
}

export interface AppointmentState {
  estimates: Array<{ [key: string]: any }>
}
export interface JobState {
  estimates: Array<{ [key: string]: any }>
}
export interface FileAttachment {
  name: string
  type: string
  url: string
  data?: File | Blob
}

export interface TwilioState {
  twilioData: { [key: string]: any }
  showTwilioAlert: boolean
  showTwilioLocationAlert: boolean
  showTwilioAgencyLimitAlert: boolean
  locationTwilioEmpty: boolean
  showResolvePrompt: boolean
}

export interface Folder {
  folderId: string
  locationId: string
}

export interface FolderState {
  FolderItems: Array<{ [key: string]: Folder }>
}

export interface MembershipState {
  locationId: string
  offers: Array<{ [key: string]: string }>
  products: Array<{ [key: string]: string }>
  domainInfo: any
  clientPortalInfo: any
  builderSettings: IBuilderSettings
}

export interface LocationCustomFieldsState {
  locationId: string
  customFields: Array<{ [key: string]: string }>
  customFieldFolders: Array<{ [key: string]: string }>
}

export interface CustomValuesState {
  locationId: string
  customValues: Array<{ [key: string]: string }>
}

export interface TeamState {
  teams: Team[]
  locationId: undefined | string
}

export interface IframeState {
  handshake: { [key: string]: any }
  onboardingPassword: boolean
}

export interface ProductsState {
  products: { [key: string]: any }
}
export interface StripeConnectState {
  accountId: undefined | string
  locationId: undefined | string
}

export interface IntegrationsState {
  integrations: { [key: string]: any }
  locationId: undefined | string
  showGBPDeprecationWarning: boolean
}
export interface LevelUpDayState {
  featuresActive: boolean
  levelupDate: Date
}
export interface SideBarV2 {
  version: string
  notification: string
}

export interface WhatsAppState {
  locationSetupDetails: {
    id: undefined | string
    status: undefined | string
    companyName: undefined | string
    companyWebsite: undefined | string
    companyEmail: undefined | string
  }
  numbers: Array<{
    status: undefined | string
    id: undefined | string
    number: undefined | string
    displayName: undefined | string
    logo: undefined | string
    disabled: boolean
  }>
}

export interface FeatureFlags {
  locationFeatureFlags: { [key: string]: any }
  companyFeatureFlags: { [key: string]: any }
}

export interface CustomObjectsStore {
  schemas: CustomObjectsSchema[]
  schemaByKeys: { [key: string]: CustomObjectsSchema }
}

export interface CustomObjectsSchema {
  id: string
  labels: {
    singular: string
    plural: string
  }
  description: string
  requiredProperties: string[]
  searchableProperties: string[]
  uniqueProperties: string[]
  primaryDisplayProperty: string
  key: string
  locationId: string
  type?: string
}

export interface TwilioAccountState {
  twilioAccount: Array<{ [key: string]: string }>
  locationId: string
}

export interface ConversationChannelsState {
  customConversationChannels?: ConversationProvider[]
}
export interface LabsFeatures {
  features: Array<{ [key: string]: string }>
  fetched: Boolean
  companyFeatures: Array<{ [key: string]: string }>
  companyFetched: Boolean
}

export interface OnboardingState {
  onboardingInfo?: object,
  emailVerified?: boolean
  phoneNumberVerified?: boolean
	widgetState?: boolean,
	version?: object,
	widgetPosition?: {
		x: number,
		y: number
	}
}

// can be used in future to add states
export interface LaunchpadState {
  stepInfo?: object
  isAgencyLaunchpadAssigned: boolean
  agencySegmentDetails: any
  isPiPVisible: boolean
}

export interface CommunitiesState {
  locationData: { [key: string]: any }
  usersData: { [key: string]: any }
  groups: { [key: string]: any }
  locationId: string
}
export interface LocationSettingState {
  [key: string]: boolean
}

export interface ReputationState {
  widget: {
    isEditFormEnabled: boolean
  }
}

export interface YextState {
  isEditListingFormEnabled: boolean
}

export interface SeoState {
  isSeoEnabled: boolean
}

export interface WordpressState {
  productName: string
  resellingProduct: object
}

export interface FreshChatState {
  userData: User
  companyData: Company
}

export interface CustomPages {
  customPages: any
  locationId?: string
}

export interface CustomMenus {
  customMenus: any
  totalLinks: any
}

export interface AdPublishingState {
  adPublishingLocationConfig: { [key: string]: any }
}

export interface CalendarsLocationState {
  calendarsLocationConfig: { [key: string]: any }
}

export interface IReportingNotificationState {
  firstLoadCompleted: boolean
  loading: boolean
  showNotification: boolean
  forceClosedBanner: boolean
  allNotificationData: IReportingNotification[]
  conditionFilteredNotifications: IReportingNotificationViewed[]
  apiFilteredNotifications: IReportingNotificationViewed[]
  visibleBannerNotifications: IReportingNotificationViewed[]
  visibleDrawerNotifications: IReportingNotificationViewed[],
  currentLocationId: string
  showNoNetworkNotification: boolean
  pendoViewedNotificationIds: string[]
}

export interface LocationMapSearchState {
  companyId: string,
  places: string[],
  searchString: string,
  locationId: string,
  googleSearchResults: any[],
  token: string,
}

export interface MarketplaceState {
  settings: {
    allowMarketplace: boolean
    allowNewPublicApps: boolean
    clientBlackList: string[]
    company: string
    enableLeadConnector: boolean
    enableThirdParty: boolean
    featureFlags: { [key: string]: any }
    hideLabel: boolean
    showAllApps: boolean
  }
}

export interface SupportPreferencesState {
  showLockout: boolean
}